import {
  watch, type WatchHandle,
  type WatchOptions,
  type WatchSource
} from 'vue'

type Options = Omit<WatchOptions, 'once'>

// Overloads
export function watchUntil<T>(source: WatchSource<T>, effect: (value: NonNullable<T>) => void): WatchHandle
export function watchUntil<T>(source: WatchSource<T>, effect: (value: NonNullable<T>) => void, options?: Options): WatchHandle
export function watchUntil<T>(source: WatchSource<T>, effect: (value: T) => void, ready: (value: T) => boolean): WatchHandle
export function watchUntil<T>(source: WatchSource<T>, effect: (value: T) => void, ready: (value: T) => boolean, options?: Options): WatchHandle

// Implementation.
export function watchUntil<T>(
  source: WatchSource<T>,
  effect: (value: T) => void,
  ready?: ((value: T) => boolean) | Options,
  options?: Omit<WatchOptions, 'once'>): WatchHandle {
  // Options are provided in `ready`.
  if (ready !== undefined && typeof ready !== 'function') {
    options = ready
    ready = undefined
  }
  // Fallback for `ready`.
  if (ready === undefined) {
    ready = x => x !== undefined && x !== null
  }

  let hasExecuted = false
  let stop: WatchHandle | undefined = undefined

  stop = watch(
    source,
    value => {
      if (hasExecuted) {
        stop?.()
        return
      }
      if (!ready(value)) {
        return
      }
      hasExecuted = true
      effect(value)
      stop?.()
    },
    options
  )

  return stop
}
